import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import BridgeClubLayout from '../../components/BridgeClub/BridgeClubLayout';
import { bridgeClubAPI } from '../../services/bridgeClubAPI';
import { useLocalAccess } from '../../hooks/useLocalAccess';
import './LocalOrderDetail.css';

interface ProductInfo {
  id: number;
  name: string;
  sku?: string;
  size_lbs?: string;
  source_type?: string;
}

interface Product {
  id: number;
  name: string;
  brand?: string;
  size_lbs?: string;
}

interface OrderItem {
  id: number;
  product: Product | null;
  product_info: ProductInfo | null;
  quantity: number;
  unit_price: string;
  line_total: string;
}

interface Order {
  id: number;
  order_number: string;
  status: string;
  pickup_method: string;
  subtotal: string;
  convenience_fee: string;
  tax: string;
  total: string;
  payment_status: string;
  payment_link?: string;
  payment_method?: string;
  // Set when staff has assigned a locker. The backend masks this to
  // null once status is picked_up/cancelled (see BridgeClubOrderSerializer
  // .get_locker_passcode); UI also gates on status here as a belt-and-
  // suspenders against any client that has a stale copy of the order.
  locker_passcode?: string | null;
  locker_assigned_at?: string;
  locker_number?: number | string | null;
  locker_name?: string | null;
  paid_at?: string | null;
  notes: string;
  created_at: string;
  items: OrderItem[];
}

// Polling cadence and total budget for the post-checkout confirmation
// loop. Gmail parser runs every 5 minutes on cron; check-payment-now
// fires it sooner. 30 attempts × 3 s = 90 s total — long enough for the
// receipt to land + parser to process, short enough that we don't
// wedge the page if Helcim never emails.
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 30;
const BANNER_AUTO_CLEAR_MS = 30000;

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pending: { label: 'Pending', color: '#f5a623' },
  awaiting_payment: { label: 'Awaiting Payment', color: '#f5a623' },
  paid: { label: 'Preparing', color: '#2d5a27' },
  ready_for_pickup: { label: 'Ready for Pickup', color: '#2d5a27' },
  picked_up: { label: 'Picked Up', color: '#666' },
  cancelled: { label: 'Cancelled', color: '#dc3545' },
};

/**
 * Locker pickup block shown to the customer when their order is ready
 * but not yet picked up. Big locker number + big tappable code +
 * step-by-step instructions. Designed for a customer standing in a
 * parking lot in glare, holding a phone.
 *
 * Tap-to-copy on the code uses navigator.clipboard.writeText; if the
 * browser lacks that API (older mobile WebViews) we fall back to a
 * hidden <textarea> + document.execCommand('copy'), and if that fails
 * too, we just shrug — the code is right there on screen.
 */
interface LockerPickupBlockProps {
  lockerNumber?: number | string | null;
  lockerName?: string | null;
  passcode: string;
}

const LockerPickupBlock: React.FC<LockerPickupBlockProps> = ({
  lockerNumber,
  lockerName,
  passcode,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    let ok = false;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(passcode);
        ok = true;
      } else {
        const ta = document.createElement('textarea');
        ta.value = passcode;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand('copy');
        document.body.removeChild(ta);
      }
    } catch {
      ok = false;
    }
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    }
  }, [passcode]);

  return (
    <div className="locker-pickup-block">
      <div className="locker-pickup-status">✓ READY FOR PICKUP</div>
      {lockerNumber != null && (
        <div className="locker-pickup-locker">
          <div className="locker-pickup-locker-num">Locker {lockerNumber}</div>
          {lockerName && lockerName !== `Locker ${lockerNumber}` && (
            <div className="locker-pickup-locker-name">{lockerName}</div>
          )}
        </div>
      )}

      <div className="locker-pickup-code-label">Your Code:</div>
      <button
        type="button"
        className="locker-pickup-code"
        onClick={handleCopy}
        aria-label={`Copy code ${passcode}`}
      >
        <span className="locker-pickup-code-value">{passcode}</span>
        <span className="locker-pickup-code-hint">
          {copied ? 'Copied!' : 'Tap to copy'}
        </span>
      </button>

      <div className="locker-pickup-howto">
        <h3>How to pick up:</h3>
        <ol>
          <li>Drive to the lockers behind the store</li>
          {lockerNumber != null
            ? <li>Find <strong>Locker {lockerNumber}</strong></li>
            : <li>Find your assigned locker</li>}
          <li>Enter <strong>{passcode}</strong> on the keypad</li>
          <li>The locker will unlock — grab your items</li>
          <li>Close the locker — that's it!</li>
        </ol>
      </div>
    </div>
  );
};


const LocalOrderDetail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { hasLocalAccess } = useLocalAccess();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Confirmation banner state. Three banner kinds:
  //   'confirming'  amber — "Confirming your payment…"
  //   'confirmed'   green — "Order placed and paid successfully!"
  //   'processing'  amber — "Your payment is being processed. You'll receive a confirmation email."
  //   null          no banner
  // Set when the customer arrives from the cart after Helcim Pay
  // (location.state.paymentOutcome) and cleared after 30 s OR on next
  // page view. Polling drives the 'confirming' → 'confirmed' transition.
  const [confirmationBanner, setConfirmationBanner] = useState<
    null | 'confirming' | 'confirmed' | 'processing'
  >(null);
  const pollIntervalRef = useRef<number | null>(null);
  const pollAttemptsRef = useRef(0);
  const bannerTimeoutRef = useRef<number | null>(null);

  // Read the cart's payment outcome (success / failure / unknown) from
  // router location state. Only fires once per arrival; we then strip
  // the state via history.replaceState so refreshes don't re-show.
  const initialOutcome = (location.state as any)?.paymentOutcome as
    | 'success'
    | 'failure'
    | 'unknown'
    | undefined;

  useEffect(() => {
    if (!hasLocalAccess) {
      navigate('/bridge-club');
      return;
    }
    if (id) {
      loadOrder(parseInt(id, 10));
    }
  }, [hasLocalAccess, navigate, id]);

  const loadOrder = async (orderId: number) => {
    try {
      setLoading(true);
      const data = await bridgeClubAPI.getLocalOrder(orderId);
      setOrder(data);
    } catch (err) {
      console.error('Failed to load order:', err);
    } finally {
      setLoading(false);
    }
  };

  // Lightweight reload that doesn't toggle the loading spinner —
  // used by the post-checkout poll so the page doesn't flicker.
  const refreshOrder = useCallback(async (orderId: number) => {
    try {
      const data = await bridgeClubAPI.getLocalOrder(orderId);
      setOrder(data);
      return data;
    } catch (err) {
      console.error('Failed to refresh order:', err);
      return null;
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current !== null) {
      window.clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    pollAttemptsRef.current = 0;
  }, []);

  // Schedule the banner to auto-clear after 30 s of confirmed/processing.
  const scheduleBannerClear = useCallback(() => {
    if (bannerTimeoutRef.current !== null) {
      window.clearTimeout(bannerTimeoutRef.current);
    }
    bannerTimeoutRef.current = window.setTimeout(() => {
      setConfirmationBanner(null);
      bannerTimeoutRef.current = null;
    }, BANNER_AUTO_CLEAR_MS);
  }, []);

  // Post-checkout poll. Runs only when the customer just arrived from
  // the cart with paymentOutcome != 'failure'. Stops on first paid_at
  // observation or after POLL_MAX_ATTEMPTS.
  useEffect(() => {
    if (!id || !initialOutcome) return;
    if (initialOutcome === 'failure') {
      setConfirmationBanner(null);
      setPaymentMessage({
        type: 'error',
        text: 'Payment was declined. Please try again.',
      });
      return;
    }

    setConfirmationBanner('confirming');

    // Fire the on-demand parser run (best-effort, ignore failures —
    // the 5-min scheduled task is the safety net).
    bridgeClubAPI.checkPaymentNow(parseInt(id, 10)).catch((err) => {
      console.warn('check-payment-now failed:', err);
    });

    pollAttemptsRef.current = 0;
    pollIntervalRef.current = window.setInterval(async () => {
      pollAttemptsRef.current += 1;
      const fresh = await refreshOrder(parseInt(id, 10));
      if (fresh && fresh.paid_at) {
        stopPolling();
        setConfirmationBanner('confirmed');
        scheduleBannerClear();
        return;
      }
      if (pollAttemptsRef.current >= POLL_MAX_ATTEMPTS) {
        stopPolling();
        setConfirmationBanner('processing');
        scheduleBannerClear();
      }
    }, POLL_INTERVAL_MS);

    // Clear the location state so a refresh / back-button doesn't
    // re-trigger the poll. Belt-and-braces: history.replaceState is
    // safer than react-router's navigate(..., {replace: true}) here
    // because we don't want to disturb the URL.
    try {
      window.history.replaceState({}, document.title);
    } catch {}

    return () => {
      stopPolling();
      if (bannerTimeoutRef.current !== null) {
        window.clearTimeout(bannerTimeoutRef.current);
        bannerTimeoutRef.current = null;
      }
    };
    // initialOutcome is a one-shot from router state; intentionally
    // not in deps so we don't re-fire on parent re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Listen for Helcim Pay modal completion.
  //
  // Architectural note: this handler is a UX fast-path only. The
  // AUTHORITATIVE paid signal is the Gmail celery worker on the
  // backend (feedmill.tasks._handle_bc_payment), which parses the
  // Helcim receipt email that lands in redbridgepayments@gmail.com.
  // If we fail to record here, the parser catches up within ~5 min
  // and flips the order to paid then. RecordHelcimPaymentView is
  // idempotent — if the parser wins the race, we no-op.
  //
  // Parse pattern mirrors PortalBilling.tsx — HelcimPay.js nests the
  // real transactionId / amount inside `eventMessage` as a JSON
  // string; reading top-level keys alone returns undefined.
  const handleHelcimMessage = useCallback((event: MessageEvent) => {
    if (!event.origin.includes('helcim.com')) return;
    const data = event.data;
    if (!data || typeof data !== 'object') return;

    let payload: Record<string, any> = data;
    if (typeof data.eventMessage === 'string') {
      try {
        const parsed = JSON.parse(data.eventMessage);
        const txn = parsed?.data?.data || parsed?.data || parsed;
        if (txn && typeof txn === 'object') {
          payload = { ...data, ...txn };
        }
      } catch (e) {
        console.warn('Could not parse Helcim eventMessage:', e);
      }
    }

    const statusStr = String(payload.eventStatus || payload.status || '').toUpperCase();
    const nameStr = String(payload.eventName || '');
    const isSuccess =
      nameStr === 'helcim-pay-success' ||
      (nameStr.startsWith('helcim-pay-') && statusStr === 'SUCCESS') ||
      statusStr === 'APPROVED' ||
      statusStr === 'SUCCESS' ||
      statusStr === 'COMPLETED';
    const isFailure =
      nameStr === 'helcim-pay-error' ||
      statusStr === 'DECLINED' ||
      statusStr === 'ERROR' ||
      statusStr === 'ABORTED';

    if (isSuccess) {
      const txnId =
        payload.transactionId ||
        payload.transId ||
        payload.transaction_id ||
        payload.response?.transactionId ||
        '';
      const amount =
        payload.amount ||
        payload.response?.amount ||
        order?.total;

      if (!txnId || !id) {
        // No txn id in the postMessage — leave the order awaiting_payment
        // and rely on the Gmail parser (authoritative path) to flip it
        // when the receipt email lands.
        setPaymentMessage({
          type: 'success',
          text:
            'Payment successful! Your order is being prepared. ' +
            '(Confirmation arriving shortly.)',
        });
        if (id) loadOrder(parseInt(id, 10));
        return;
      }

      // UX fast-path: record server-side with verified Helcim API call.
      // Backend is idempotent against the Gmail-parser arrival.
      bridgeClubAPI
        .recordHelcimPayment(parseInt(id, 10), {
          transaction_id: String(txnId),
          amount,
        })
        .then(() => {
          setPaymentMessage({
            type: 'success',
            text: 'Payment confirmed! Your order is being prepared.',
          });
          loadOrder(parseInt(id, 10));
        })
        .catch((err) => {
          // Recording failed — Gmail parser will catch up within ~5 min.
          // Don't show a hard error because the customer's card may have
          // genuinely been charged; soft success message + reload.
          console.error('record-helcim-payment failed:', err);
          setPaymentMessage({
            type: 'success',
            text:
              'Payment received. Confirmation is being processed and your ' +
              'order will show as paid within a few minutes.',
          });
          loadOrder(parseInt(id, 10));
        });
    } else if (isFailure) {
      setPaymentMessage({
        type: 'error',
        text: data.error || data.message || 'Payment was declined. Please try again.',
      });
    }
    // helcim-pay-close / ABORTED / CANCELLED / any other status:
    // do NOT call record-payment, do NOT flip order state.
  }, [id, order]);

  useEffect(() => {
    window.addEventListener('message', handleHelcimMessage);
    return () => window.removeEventListener('message', handleHelcimMessage);
  }, [handleHelcimMessage]);

  const openHelcimPayment = (checkoutToken: string) => {
    setPaymentMessage(null);
    if (window.appendHelcimPayIframe) {
      window.appendHelcimPayIframe(checkoutToken);
    }
  };

  const handleRetryPayment = async () => {
    if (!order) return;
    try {
      setRetrying(true);
      setPaymentMessage(null);
      const result = await bridgeClubAPI.retryPaymentLink(order.id);
      if (result.checkout_token && window.appendHelcimPayIframe) {
        setOrder({ ...order, payment_link: result.payment_link });
        openHelcimPayment(result.checkout_token);
      } else if (result.payment_link) {
        // Fallback if appendHelcimPayIframe not available
        setOrder({ ...order, payment_link: result.payment_link });
        window.open(result.payment_link, '_blank');
      }
    } catch (err) {
      console.error('Failed to generate payment link:', err);
      setPaymentMessage({ type: 'error', text: 'Failed to generate payment. Please try again.' });
    } finally {
      setRetrying(false);
    }
  };

  const getItemName = (item: OrderItem): string => {
    if (item.product_info) return item.product_info.name;
    if (item.product) return item.product.name;
    return 'Unknown Item';
  };

  const getItemBrand = (item: OrderItem): string | null => {
    if (item.product?.brand) return item.product.brand;
    return null;
  };

  const getItemSize = (item: OrderItem): string | null => {
    if (item.product_info?.size_lbs) return item.product_info.size_lbs;
    if (item.product?.size_lbs) return item.product.size_lbs;
    return null;
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (!hasLocalAccess) {
    return null;
  }

  if (loading) {
    return (
      <BridgeClubLayout>
        <div className="local-order-detail">
          <div className="order-loading">
            <div className="spinner"></div>
            <p>Loading order...</p>
          </div>
        </div>
      </BridgeClubLayout>
    );
  }

  if (!order) {
    return (
      <BridgeClubLayout>
        <div className="local-order-detail">
          <div className="order-not-found">
            <p>Order not found</p>
            <Link to="/bridge-club/local-orders">Back to Orders</Link>
          </div>
        </div>
      </BridgeClubLayout>
    );
  }

  const statusInfo = STATUS_LABELS[order.status] || {
    label: order.status,
    color: '#666',
  };

  return (
    <BridgeClubLayout>
      <div className="local-order-detail">
        <div className="order-header">
          <Link to="/bridge-club/local-orders" className="back-link">
            Back to Orders
          </Link>
          <h1>{order.order_number}</h1>
          <span
            className="order-status-badge"
            style={{ background: statusInfo.color }}
          >
            {statusInfo.label}
          </span>
        </div>

        <div className="order-date">{formatDate(order.created_at)}</div>

        {/* Post-checkout confirmation banner */}
        {confirmationBanner === 'confirming' && (
          <div
            role="status"
            style={{
              padding: '14px 18px',
              borderRadius: 8,
              marginBottom: 16,
              background: '#fef3c7',
              color: '#92400e',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <span
              style={{
                width: 14,
                height: 14,
                borderRadius: '50%',
                border: '2px solid #92400e',
                borderTopColor: 'transparent',
                animation: 'spin 0.8s linear infinite',
              }}
              aria-hidden="true"
            />
            <span>Confirming your payment… This usually takes about a minute.</span>
          </div>
        )}
        {confirmationBanner === 'confirmed' && (
          <div
            role="status"
            style={{
              padding: '14px 18px',
              borderRadius: 8,
              marginBottom: 16,
              background: '#dcfce7',
              color: '#166534',
              fontWeight: 600,
            }}
          >
            ✓ Order placed and paid successfully!
          </div>
        )}
        {confirmationBanner === 'processing' && (
          <div
            role="status"
            style={{
              padding: '14px 18px',
              borderRadius: 8,
              marginBottom: 16,
              background: '#fef3c7',
              color: '#92400e',
              fontWeight: 600,
            }}
          >
            Your payment is being processed. You'll receive a confirmation email when it clears.
          </div>
        )}

        {/* Locker pickup block.
            ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
            Two display modes:
              - PRE-PICKUP (status=ready_for_pickup, code present):
                Big locker number + tappable 6-digit code + step-by-step
                instructions. Designed to be readable in a parking lot.
              - POST-PICKUP (status=picked_up):
                Thank-you confirmation; code is hidden (and the API
                masks it anyway).
            Server returns locker_passcode=null once status is picked_up,
            so the truthy check below is the second layer of defense.
        */}
        {order.pickup_method === 'locker' && order.status === 'ready_for_pickup' && order.locker_passcode && (
          <LockerPickupBlock
            lockerNumber={order.locker_number}
            lockerName={order.locker_name}
            passcode={order.locker_passcode}
          />
        )}
        {order.pickup_method === 'locker' && order.status === 'picked_up' && (
          <div className="locker-picked-up">
            <div className="locker-picked-up-icon" aria-hidden="true">✓</div>
            <h2>Order Picked Up</h2>
            <p>Thanks for stopping by — we hope your animals enjoy the feed!</p>
          </div>
        )}

        {/* Payment Message */}
        {paymentMessage && (
          <div className={`payment-message ${paymentMessage.type}`} style={{
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '16px',
            background: paymentMessage.type === 'success' ? '#e8f5e9' : '#fce4ec',
            color: paymentMessage.type === 'success' ? '#2d5a27' : '#c62828',
            fontWeight: 500,
          }}>
            {paymentMessage.text}
          </div>
        )}

        {/* Payment Section */}
        {order.payment_status !== 'paid' && (
          <div className="payment-section">
            {order.payment_method && ['card', 'ach'].includes(order.payment_method) ? (
              <button
                onClick={handleRetryPayment}
                disabled={retrying}
                className="pay-btn"
              >
                {retrying ? 'Opening payment...' : 'Pay Now'}
              </button>
            ) : (
              <div className="payment-pending">
                <p className="payment-pending-text">Payment pending</p>
              </div>
            )}
          </div>
        )}

        {/* Order Items */}
        <div className="order-items-section">
          <h2>Items</h2>
          <div className="order-items">
            {order.items.map((item) => (
              <div key={item.id} className="order-item">
                <div className="item-info">
                  {getItemBrand(item) && (
                    <span className="item-brand">{getItemBrand(item)}</span>
                  )}
                  <span className="item-name">{getItemName(item)}</span>
                  {getItemSize(item) && (
                    <span className="item-size">{getItemSize(item)} lbs</span>
                  )}
                </div>
                <div className="item-qty-price">
                  <span className="item-qty">x{item.quantity}</span>
                  <span className="item-price">${item.line_total}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Order Summary */}
        <div className="order-summary">
          <div className="summary-row">
            <span>Subtotal</span>
            <span>${order.subtotal}</span>
          </div>
          {parseFloat(order.convenience_fee) > 0 && (
            <div className="summary-row">
              <span>Locker Fee</span>
              <span>${order.convenience_fee}</span>
            </div>
          )}
          <div className="summary-row">
            <span>Tax</span>
            <span>${order.tax}</span>
          </div>
          <div className="summary-row summary-total">
            <span>Total</span>
            <span>${order.total}</span>
          </div>
        </div>

        {/* Order Details */}
        <div className="order-info">
          <div className="info-row">
            <span>Pickup Method</span>
            <span>{order.pickup_method === 'locker' ? '24/7 Locker' : 'In-Store'}</span>
          </div>
          <div className="info-row">
            <span>Payment Status</span>
            <span style={{ textTransform: 'capitalize' }}>{order.payment_status?.replace('_', ' ') || 'Pending'}</span>
          </div>
        </div>

        {order.notes && (
          <div className="order-notes">
            <h3>Notes</h3>
            <p>{order.notes}</p>
          </div>
        )}
      </div>
    </BridgeClubLayout>
  );
};

export default LocalOrderDetail;
